import { useId, useState } from 'react';
import { useMutation, useQuery, type ApolloError } from '@apollo/client';
import { CheckCircle2, CircleAlert, Github } from 'lucide-react';
import { SYNC_STATUS_QUERY } from '../graphql/queries';
import {
  CONNECT_GITHUB_REPO_MUTATION,
  DISCONNECT_GITHUB_REPO_MUTATION,
} from '../graphql/mutations';
import { ConfirmDialog } from './ConfirmDialog';

export interface SyncStatus {
  available: boolean;
  connected: boolean;
  repoFullName: string | null;
  branch: string | null;
  state: string | null;
  attentionReason: string | null;
  lastSyncedAt: string | null;
}

interface SyncStatusData {
  syncStatus: SyncStatus;
}

/** "today at 14:05", "Jul 8 at 09:12" — calm, local. */
function friendlySyncedAt(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const time = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  if (date.toDateString() === new Date().toDateString()) return `today at ${time}`;
  return `${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} at ${time}`;
}

/** The calm message for a failed connect: server messages are written for users. */
function connectErrorMessage(error: ApolloError): string {
  const graphQLError = error.graphQLErrors?.[0];
  if (graphQLError?.message) return graphQLError.message;
  return 'Could not connect the repository. Check the details and try again.';
}

/**
 * "Sync to GitHub" — the ONE place git vocabulary is allowed in the app
 * (GIT_SYNC_PLAN decision 2): an opt-in power-user card on Profile. Everywhere
 * else sync surfaces only as the documents-page attention banner that links
 * here. States: not configured (server keeps the feature off) → the card
 * hides; unconnected → intro + connect form; connected → status + disconnect.
 */
export function GithubSyncCard() {
  const formId = useId();
  const { data, loading, error, refetch } = useQuery<SyncStatusData>(SYNC_STATUS_QUERY);
  const [formOpen, setFormOpen] = useState(false);
  const [repoFullName, setRepoFullName] = useState('');
  const [branch, setBranch] = useState('main');
  const [token, setToken] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [connect, { loading: connecting }] = useMutation(CONNECT_GITHUB_REPO_MUTATION, {
    onError: (mutationError) => setFormError(connectErrorMessage(mutationError)),
    onCompleted: () => {
      setFormOpen(false);
      setToken('');
      setFormError(null);
      void refetch();
    },
  });
  const [disconnect, { loading: disconnecting }] = useMutation(DISCONNECT_GITHUB_REPO_MUTATION, {
    onCompleted: () => void refetch(),
    onError: () => void refetch(),
  });

  // The feature is off on this deployment, or the status can't load — either
  // way this stays a quiet non-surface rather than an error card.
  if (error) return null;
  if (data && !data.syncStatus.available) return null;

  const status = data?.syncStatus;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const repo = repoFullName.trim();
    if (!/^[^\s/]+\/[^\s/]+$/.test(repo)) {
      setFormError('The repository is written as owner/name, e.g. daniel/wiki.');
      return;
    }
    if (!token.trim()) {
      setFormError('A GitHub token is needed to reach the repository.');
      return;
    }
    void connect({
      variables: { repoFullName: repo, branch: branch.trim() || 'main', token: token.trim() },
    });
  };

  return (
    <section
      aria-label="Sync to GitHub"
      className="glass-panel rounded-2xl p-5 sm:p-6 border border-[var(--card-border)] shadow-soft animate-fade-in-up"
    >
      <div className="flex items-center gap-3 mb-1">
        <Github className="w-5 h-5 text-[var(--text-secondary)] shrink-0" aria-hidden />
        <h2 className="text-lg font-semibold text-[var(--text-primary)]">Sync to GitHub</h2>
      </div>

      {loading && <span className="skeleton block h-12 w-full rounded-xl" aria-hidden />}

      {!loading && status && !status.connected && (
        <>
          <p className="text-sm text-[var(--text-secondary)]">
            Keep your documents in a GitHub repository you own. Edits flow both ways: what you write
            here becomes a commit, and commits made elsewhere show up here.
          </p>
          {!formOpen && (
            <button
              type="button"
              className="btn btn-primary mt-4"
              onClick={() => setFormOpen(true)}
            >
              Connect a repository
            </button>
          )}
          {formOpen && (
            <form
              onSubmit={handleSubmit}
              className="mt-4 space-y-4"
              aria-label="Connect a repository"
            >
              <div>
                <label htmlFor={`${formId}-repo`} className="label">
                  Repository
                </label>
                <input
                  id={`${formId}-repo`}
                  type="text"
                  value={repoFullName}
                  onChange={(e) => setRepoFullName(e.target.value)}
                  placeholder="owner/name"
                  autoComplete="off"
                  className="mt-1"
                />
              </div>
              <div>
                <label htmlFor={`${formId}-branch`} className="label">
                  Branch
                </label>
                <input
                  id={`${formId}-branch`}
                  type="text"
                  value={branch}
                  onChange={(e) => setBranch(e.target.value)}
                  autoComplete="off"
                  className="mt-1"
                />
              </div>
              <div>
                <label htmlFor={`${formId}-token`} className="label">
                  Access token
                </label>
                <input
                  id={`${formId}-token`}
                  type="password"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="github_pat_…"
                  autoComplete="off"
                  className="mt-1"
                />
                <p className="mt-1 text-xs text-[var(--text-tertiary)]">
                  A fine-grained token with read and write access to just this repository. It is
                  stored encrypted and never shown again.
                </p>
              </div>
              {formError && (
                <p role="alert" className="text-sm text-red-600">
                  {formError}
                </p>
              )}
              <div className="flex gap-2">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={connecting}
                  aria-busy={connecting}
                >
                  {connecting ? 'Connecting…' : 'Connect'}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => {
                    setFormOpen(false);
                    setFormError(null);
                  }}
                  disabled={connecting}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </>
      )}

      {!loading && status?.connected && (
        <div className="mt-2 space-y-3">
          <p className="text-sm text-[var(--text-secondary)]">
            <span className="font-mono text-[var(--text-primary)]">{status.repoFullName}</span>
            <span className="text-[var(--text-tertiary)]"> · {status.branch}</span>
          </p>
          {status.state === 'attention' ? (
            <p className="flex items-start gap-2 text-sm text-amber-700" role="status">
              <CircleAlert className="w-4 h-4 mt-0.5 shrink-0" aria-hidden />
              <span>{status.attentionReason ?? 'Sync needs attention.'}</span>
            </p>
          ) : (
            <p
              className="flex items-start gap-2 text-sm text-[var(--text-secondary)]"
              role="status"
            >
              <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" aria-hidden />
              <span>
                Synced
                {friendlySyncedAt(status.lastSyncedAt)
                  ? ` — last change ${friendlySyncedAt(status.lastSyncedAt)}`
                  : ''}
              </span>
            </p>
          )}
          <button
            type="button"
            className="btn btn-ghost text-red-600"
            onClick={() => setConfirmOpen(true)}
            disabled={disconnecting}
            aria-busy={disconnecting}
          >
            {disconnecting ? 'Disconnecting…' : 'Stop syncing'}
          </button>
          <ConfirmDialog
            open={confirmOpen}
            title="Stop syncing?"
            body="Your documents stay here and in the repository — they just stop staying in step. You can reconnect at any time."
            confirmLabel="Stop syncing"
            onConfirm={() => {
              setConfirmOpen(false);
              void disconnect();
            }}
            onCancel={() => setConfirmOpen(false)}
          />
        </div>
      )}
    </section>
  );
}
